import {
    Box,
    Checkbox,
    FormControl,
    ListItemText,
    ListSubheader,
    MenuItem,
    OutlinedInput,
    Select,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface FilterOption {
    label: string;
    value?: string;
    isHeader?: boolean;
}

export type FilterDropDownSelectionMode = 'single' | 'multi';

interface FilterDropDownProps {
    id: string;
    label: string;
    options: FilterOption[];
    value: string[];
    setValue: (value: string[]) => void;
    selectionMode?: FilterDropDownSelectionMode;
    showAllOption?: boolean;
    showEmptyOption?: boolean;
    onOptionClick?: (option: FilterOption) => void;
    disabled?: boolean;
    size?: 'small' | 'medium';
    sx?: SxProps<Theme>;
    renderValue?: (selected: string[]) => ReactNode;
}

export const FilterDropDown = ({
    id,
    label,
    options,
    value,
    setValue,
    selectionMode = 'multi',
    showAllOption = true,
    showEmptyOption = true,
    disabled = false,
    size = 'small',
    sx,
    renderValue,
    onOptionClick,
}: FilterDropDownProps) => {
    const { t } = useTranslation();
    const isMulti = selectionMode === 'multi';
    const emptyLabel = showAllOption ? t('All') : t('None');

    const labelRenderValue = (selected: string[]) => {
        if (
            selected.length === 0 ||
            (isMulti && selected.length === options.length)
        ) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box
                        component="span"
                        sx={{ color: '#757575', fontWeight: 400 }}
                    >
                        {label}
                    </Box>
                    <Box
                        component="span"
                        sx={{ color: '#424242', fontWeight: 600 }}
                    >
                        {emptyLabel}
                    </Box>
                </Box>
            );
        }

        if (selected.length === 1) {
            const option = options.find(
                (opt) => !opt.isHeader && opt.value === selected[0],
            );
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box
                        component="span"
                        sx={{ color: '#757575', fontWeight: 400 }}
                    >
                        {label}
                    </Box>
                    <Box
                        component="span"
                        sx={{ color: '#424242', fontWeight: 600 }}
                    >
                        {option ? option.label : selected[0]}
                    </Box>
                </Box>
            );
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box
                    component="span"
                    sx={{ color: '#757575', fontWeight: 400 }}
                >
                    {label}
                </Box>
                <Box
                    component="span"
                    sx={{ color: '#424242', fontWeight: 600 }}
                >
                    {selected.length} {t('selected')}
                </Box>
            </Box>
        );
    };

    const handleChange = (event: { target: { value: unknown } }) => {
        const v = event.target.value;
        if (isMulti) {
            setValue(Array.isArray(v) ? v : []);
        } else {
            setValue(v && typeof v === 'string' ? [v] : []);
        }
    };

    const selectValue = isMulti ? value : value[0] ?? '';
    const selectableOptions = options.filter(
        (o) => !o.isHeader && o.value != null,
    );
    const selectableValues = selectableOptions.map((o) => o.value);
    const allSelected =
        selectableValues.length > 0 && value.length >= selectableValues.length;

    const handleSelectDeselectAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        setValue(allSelected ? [] : selectableValues);
    };

    return (
        <FormControl
            sx={{
                minWidth: 200,
                ...sx,
            }}
            size={size}
            disabled={disabled}
        >
            <Select
                multiple={isMulti}
                id={id}
                value={selectValue}
                onChange={handleChange}
                input={<OutlinedInput />}
                renderValue={renderValue ?? (() => labelRenderValue(value))}
                displayEmpty
                data-testid={`filter-dropdown-${id}`}
                sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    backgroundColor: '#F5F5F5',
                    '& .MuiSelect-select': {
                        color: '#424242',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    },
                }}
            >
                {isMulti && selectableValues.length > 0 && (
                    <ListSubheader
                        disableSticky
                        sx={{
                            padding: '6px 16px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            lineHeight: 'inherit',
                        }}
                    >
                        <Typography
                            component="button"
                            type="button"
                            variant="body1"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleSelectDeselectAll(e);
                            }}
                            sx={{
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'text.primary',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {allSelected ? t('Deselect all') : t('Select all')}
                        </Typography>
                    </ListSubheader>
                )}
                {!isMulti && showEmptyOption && (
                    <MenuItem value="">
                        <ListItemText
                            primary={emptyLabel}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: '#424242',
                                },
                            }}
                        />
                    </MenuItem>
                )}
                {options.map((option) =>
                    option.isHeader ? (
                        <ListSubheader
                            key={`header-${option.label}`}
                            disableSticky
                            sx={{
                                typography: 'caption',
                                fontWeight: 600,
                                color: '#9E9E9E',
                                padding: '8px 16px',
                            }}
                        >
                            {option.label}
                        </ListSubheader>
                    ) : (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            onClick={() => onOptionClick?.(option)}
                            sx={{
                                padding: '8px 16px',
                                '&:hover': {
                                    backgroundColor: '#F5F5F5',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#E3F2FD',
                                    '&:hover': {
                                        backgroundColor: '#E3F2FD',
                                    },
                                },
                            }}
                        >
                            {isMulti && (
                                <Checkbox
                                    checked={
                                        !!option.value &&
                                        value.includes(option.value)
                                    }
                                    sx={{
                                        color: '#1976D2',
                                        '&.Mui-checked': {
                                            color: '#1976D2',
                                        },
                                    }}
                                />
                            )}
                            <ListItemText
                                primary={option.label}
                                sx={{
                                    '& .MuiListItemText-primary': {
                                        color: '#424242',
                                    },
                                }}
                            />
                        </MenuItem>
                    ),
                )}
            </Select>
        </FormControl>
    );
};
